import type { ClientId } from '@seaber/common/lib/clientId';
import type { EventTimeline } from '../../core/Timelines';
import type { SwapDirection } from '../../core/Core';
import { getEventResourceId } from '../../core/Core';
import type { EventOrLeg, Events, TimeData } from '../../core/TimeData';
import {
  getEventLocationId,
  isCargoEventTimeData,
  isCoaEventTimeData,
  isLeg,
  isNotTimeData,
  isOffHireEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallEventTimeData,
  isSingleEventTimeData,
  isSpotEventTimeData,
  isStaticEventTimeData,
  isTCOrderEventTimeData,
  isTimeData,
  type PortCallEvent,
  PortCallEventType,
} from '../../core/TimeData';
import type {
  TimelineGroup,
  TimelineGroupBase,
  TimelineItem,
} from '../react-calendar-timeline';
import type { ScenarioTimelineGroupFields, ScheduleItem } from '../types';
import {
  ItemType,
  type TimelineEvent,
  type TimelineOffHireEvent,
  type TimelineOpenPosition,
  type TimelineTcInEvent,
  type TimelineVesselReservation,
  type TimelineVoyage,
} from '../types';
import type {
  ScenarioTimelineDiff,
  TimelineDiffState,
} from '../../ScenarioUtil';
import { mergeTimeDataDiffs } from '../../ScenarioUtil';
import type {
  OpenPositionsFragment,
  ScenarioTimeline_OrganizationFragment,
  VesselReservationFragment,
} from '@seaber/common/graphql/types';
import type { ScenarioType } from '../ScenarioMergeTimeline';
import type { ScenarioTimelineProps } from '../ScenarioTimeline';
import type { GetIncomeForLoadedAmount } from '../../shipment/income/income';
import type { PnlData } from '../../hooks/usePnlData';
import type { Distances } from '../../distances/context';
import type { ScenarioOrder } from '../../../lib/planningData';
import type { CostSharingDetailsByPortCall } from '../../shipment/TCE';
import {
  computeTceForTimeData,
  getCostSharingDetailsForPortCalls,
} from '../../shipment/TCE';
import { getOverlappingEvents } from '../../core/util';
import {
  assertDefined,
  assertedDefined,
  exhaustiveCheck,
  sortBy,
} from '../../../lib/util';
import uniq from 'lodash/uniq';

import { getVesselCapacities } from '../../core/Vessel';
import { computeVoyagePerformanceMetrics } from '../../core/Core.voyage-metrics';
import { compact } from 'lodash';
import groupBy from 'lodash/groupBy';
import {
  freeEventFilter,
  getRelatedOrder,
  groupOrdersByPortCallId,
  isLegOrWait,
} from './utils';
import type { PnlByShipmentId } from '../../pnl/ui/context';

function getPortCallEventsByPortCall(events: TimeData[]) {
  const portCallEventsByPortCallId = new Map<
    ClientId,
    Record<PortCallEventType, TimeData<PortCallEvent>>
  >();

  events.forEach((td) => {
    if (!isPortCallEventTimeData(td) || !td.event.portCallId) return;

    portCallEventsByPortCallId.set(
      td.event.portCallId,
      Object.assign(
        {
          [PortCallEventType.Arrival]: null,
          [PortCallEventType.Berthing]: null,
          [PortCallEventType.Departure]: null,
        },
        portCallEventsByPortCallId.get(td.event.portCallId),
        {
          [td.event.event]: td,
        },
      ),
    );
  });

  return portCallEventsByPortCallId;
}

export function buildAssignedItems({
  onToggleShipment,
  expandedShipmentIds,
  onSwapScheduleEntry,
  onSwapPortCall,
  sharedOrderIds,
  timelines,
  groups,
  timelineDiff,
  organizations,
  scenarioType,
  isCompressedView,
  getIncomeForLoadedAmount,
  pnlData,
  scenarioName,
  distances,
  charteringOpenPositions,
  vesselReservations,
  euEtsCostEnabled,
  fuelEuCostEnabled,
  orderById,
  pnlByShipmentId,
}: {
  onToggleShipment?: (
    shipmentId: number | ClientId,
    isCollapsed: boolean,
  ) => void;
  ctrlKeyState?: boolean;
  expandedShipmentIds: Set<number | ClientId>;
  onSwapScheduleEntry?: (
    timelineId: EventTimeline['id'],
    indexToMove: number,
    direction: SwapDirection,
    timeDataItem: TimeData,
  ) => void;
  onSwapPortCall?: (
    timelineId: EventTimeline['id'],
    portCallId: ClientId,
    shipmentIds: ClientId[],
    direction: SwapDirection,
  ) => void;
  sharedOrderIds: Set<number>;
  timelines: EventTimeline[];
  groups: TimelineGroup<ScenarioTimelineGroupFields>[];
  timelineDiff: ScenarioTimelineDiff | null;
  organizations: Map<number, ScenarioTimeline_OrganizationFragment>;
  scenarioType?: ScenarioType;
  isCompressedView?: ScenarioTimelineProps['isCompressedView'];
  getIncomeForLoadedAmount?: GetIncomeForLoadedAmount;
  pnlData?: PnlData | null;
  scenarioName?: string | null;
  distances: Distances;
  charteringOpenPositions?: OpenPositionsFragment[];
  vesselReservations?: VesselReservationFragment[];
  euEtsCostEnabled?: boolean;
  fuelEuCostEnabled?: boolean;
  orderById: Map<number, ScenarioOrder> | null;
  pnlByShipmentId: PnlByShipmentId | undefined;
}) {
  const itemsByGroup: Map<TimelineGroupBase['id'], ScheduleItem[]> = new Map();
  const allItems: ScheduleItem[] = [];

  function addItemToGroup(
    groupId: TimelineGroupBase['id'],
    item: ScheduleItem,
  ) {
    const items = itemsByGroup.get(groupId) ?? [];
    allItems.push(item);
    items.push(item);
    if (items.length === 1) {
      itemsByGroup.set(groupId, items);
    }
  }

  const buildTimelineItem = <
    EventType extends Events & { order: ScenarioOrder },
    IT extends ItemType.COA | ItemType.SPOT | ItemType.SCHEDULED_ORDER,
  >(
    itemType: IT,
    shipmentTds: TimeData<EventType>[],
    id: string,
    isWholeShipment: boolean,
    timelineFreeEvents: EventOrLeg[],
    group: TimelineGroup<ScenarioTimelineGroupFields>,
    organizations: ScenarioTimelineProps['organizations'],
    batchedFreeEvents: Set<EventOrLeg> | undefined,
    portCallCostSharingDetails: CostSharingDetailsByPortCall,
    isCompressedView: ScenarioTimelineProps['isCompressedView'],
    isFirstShipmentEvent: boolean,
    orderById: Map<number, ScenarioOrder> | null,
  ): TimelineItem<TimelineVoyage> => {
    const overlappingEvents = getOverlappingEvents(
      shipmentTds,
      timelineFreeEvents,
      true,
    );

    if (batchedFreeEvents && isWholeShipment) {
      overlappingEvents.forEach((e) => batchedFreeEvents.add(e));
    }
    const orderEventsWithGlobals = sortBy(
      [...shipmentTds, ...overlappingEvents],
      (x) => x.start,
    );

    const includedOrders = uniq(shipmentTds.map((td) => td.event.order));
    const shipmentId = includedOrders[0].shipmentId;

    const groupId = group.id;
    const prevItem = (itemsByGroup.get(groupId) ?? []).at(-1);

    const filteredTimeDataEvents = orderEventsWithGlobals.filter(isTimeData);
    const filteredOrderEventsGlobals =
      filteredTimeDataEvents.filter(isCargoEventTimeData) || [];

    const prevItemEvents = (
      prevItem && 'events' in prevItem ? prevItem.events : []
    ).filter(isCargoEventTimeData);

    const prevItemLocation = getEventLocationId(
      prevItemEvents[prevItemEvents.length - 1]?.event || {},
    );
    const currentItemLocation = getEventLocationId(
      filteredOrderEventsGlobals[0]?.event || {},
    );

    const shipmentPnl = shipmentId
      ? pnlByShipmentId?.get(shipmentId)
      : undefined;

    const tceData =
      getIncomeForLoadedAmount &&
      pnlData &&
      isWholeShipment &&
      shipmentId &&
      shipmentPnl
        ? computeTceForTimeData(
            orderEventsWithGlobals,
            shipmentId,
            getIncomeForLoadedAmount,
            distances,
            {
              euEtsCostEnabled: euEtsCostEnabled ?? false,
              fuelEuCostEnabled: fuelEuCostEnabled ?? false,
              outgoingCoaId: group.timeline.coaId ?? null,
              pnlData,
              portCallCostSharingDetails,
            },
            orderById,
            shipmentPnl,
          )
        : undefined;

    const vessel = group.timeline.vessel;
    const capacities = vessel && getVesselCapacities(vessel);
    const metrics =
      capacities &&
      computeVoyagePerformanceMetrics(
        filteredTimeDataEvents,
        distances,
        capacities.massCapacity,
      );

    const orderTdDiffStates = shipmentTds.map((td): TimelineDiffState => {
      const entryState =
        timelineDiff?.entriesState.get(td.event.entry.clientId) ?? null;
      const { order } = td.event;
      const orderState = timelineDiff?.ordersState.get(order.id) ?? null;
      const shipmentState =
        (order.shipment &&
          timelineDiff?.shipmentToOrdersState.get(order.shipment.clientId)) ??
        null;

      return mergeTimeDataDiffs(entryState, orderState, shipmentState);
    });
    const portCallTdDiffStates = compact(
      overlappingEvents
        .filter(isPortCallEventTimeData)
        .map((td): TimelineDiffState | undefined => {
          return timelineDiff?.entriesState.get(td.event.entry.clientId);
        }),
    );

    const combinedDiff = mergeTimeDataDiffs(
      ...orderTdDiffStates,
      ...portCallTdDiffStates,
    );

    const legDataEvents = orderEventsWithGlobals.filter(isNotTimeData);
    const start_time =
      prevItem?.group === group.id && prevItemLocation === currentItemLocation
        ? shipmentTds[0]?.start
        : (
            orderEventsWithGlobals.find(isPortCallBerthingEventTimeData) ||
            filteredOrderEventsGlobals.find((td) => !isLeg(td)) ||
            filteredOrderEventsGlobals[0]
          )?.start;

    return {
      id: scenarioType ? `${scenarioType}${scenarioName ?? ''}-${id}` : id,
      orders: includedOrders,
      type: itemType,
      isCollapsed: isWholeShipment,
      group: group.id,
      groupObj: group,
      start_time,
      end_time: Math.max(...shipmentTds.map((td) => td.end)),
      events: filteredTimeDataEvents,
      implicitEvents: legDataEvents,
      onToggleShipment:
        shipmentId === null || !onToggleShipment
          ? undefined
          : (isCollapsed) => onToggleShipment(shipmentId, isCollapsed),
      canChangeGroup:
        shipmentId === null || !expandedShipmentIds.has(shipmentId),
      isToggleExpanded: !!(shipmentId && expandedShipmentIds.has(shipmentId)),
      organizations,
      onSwapScheduleEntry,
      onSwapPortCall,
      isCompressedView,
      isShared: includedOrders.every((o) => sharedOrderIds.has(o.id)),
      enablePortCallUpdates: true,
      enableVoyageCharterUpdates: true,
      combinedDiff,
      scenarioType,
      tceData,
      metrics: metrics ?? undefined,
      isFirstShipmentEvent,
    };
  };

  const batchedFreeEvents = new Set<EventOrLeg>();

  const buildItemBatches = <
    EventType extends Events & { order: ScenarioOrder },
    T extends Exclude<Events, EventType>,
  >(
    timeline: EventTimeline,
    mainEventFilter: (td: TimeData<T | EventType>) => td is TimeData<EventType>,
    group: TimelineGroup<ScenarioTimelineGroupFields>,
    generateItemId: (
      isBatch: boolean,
      firstEvent: EventType,
      shipmentId: string,
    ) => string,
    organizations: Map<number, ScenarioTimeline_OrganizationFragment>,
    isCompressedView: ScenarioTimelineProps['isCompressedView'],
    orderById: Map<number, ScenarioOrder> | null,
  ) => {
    assertDefined(group, `${timeline.type} group should be defined`);
    for (const voyageGroup of timeline.voyageGroups) {
      const mainEvents = voyageGroup.events.filter(mainEventFilter);
      const ordersByPortCallId = groupOrdersByPortCallId(mainEvents);
      const portCallCostSharingDetails = getCostSharingDetailsForPortCalls(
        voyageGroup.events,
      );

      const orderTdsByShipment = groupBy(mainEvents, (e) => {
        const order = getRelatedOrder(ordersByPortCallId, e.event);
        if (!order) {
          console.warn(
            `There is an event without an associated order and shipment so it cannot be grouped into any batch:`,
            e,
          );
        }
        return order?.shipmentId ?? '';
      });

      /** Events which will be included into all overlapping items on the timeline */
      const timelineFreeEvents: EventOrLeg[] = [
        ...voyageGroup.events.filter(freeEventFilter),
        ...voyageGroup.eventsAndLegs.filter(isLegOrWait),
      ];

      const sortedOrderTdsByShipment = sortBy(
        Object.values(orderTdsByShipment),
        (x) => x[0].start,
      );

      for (const shipmentTds of sortedOrderTdsByShipment) {
        const firstTd = assertedDefined(
          shipmentTds[0],
          'expected at least one td in group',
        );
        const shipmentId = firstTd.event.order.shipmentId ?? null;
        const renderIndividualOrders =
          shipmentId === null || expandedShipmentIds.has(shipmentId);

        const itemType =
          timeline.type === 'tc'
            ? ItemType.SCHEDULED_ORDER
            : timeline.type === 'coa'
              ? ItemType.COA
              : timeline.type === 'spot'
                ? ItemType.SPOT
                : null;

        assertDefined(itemType, 'itemtype');

        if (renderIndividualOrders) {
          // We need to render each order as a separate item on the timeline when the shipment is collapsed.
          const groupedTimeDataByOrder = shipmentTds.reduce<
            TimeData<EventType>[][]
          >((acc, td) => {
            const order = getRelatedOrder(ordersByPortCallId, td.event);
            const orderId = order?.id ?? '';
            const existingOrder = acc.find(
              (arr) =>
                isCargoEventTimeData(arr[0]) &&
                arr[0].event.order?.id === orderId,
            );
            if (!existingOrder) {
              acc.push([td]);
            } else {
              existingOrder.push(td);
            }
            return acc;
          }, []);

          const groupedByVoyageCharter = groupBy(
            groupedTimeDataByOrder,
            (tds) =>
              tds[0].event.order.incomingVoyageCharterRelation?.voyageCharterId,
          );

          const isSameTds = (
            tds1: TimeData<EventType>[] | undefined,
            tds2: TimeData<EventType>[] | undefined,
          ) => {
            if (!tds1 || !tds2) return false;
            if (tds1.length !== tds2.length) return false;
            return tds1.every(
              (item, index) => item.index === tds2[index].index,
            );
          };

          for (const [index, shipmentTds] of groupedTimeDataByOrder.entries()) {
            const firstEvent = shipmentTds[0].event;
            const voyageCharterId =
              firstEvent.order.incomingVoyageCharterRelation?.voyageCharterId;

            const shipmentsByVC = voyageCharterId
              ? groupedByVoyageCharter[voyageCharterId]
              : undefined;

            const isFirstShipmentEvent = shipmentsByVC
              ? isSameTds(shipmentsByVC.at(0), shipmentTds)
              : index === 0;

            const timelineItem: TimelineItem<TimelineVoyage> =
              buildTimelineItem(
                itemType,
                shipmentTds,
                generateItemId(false, firstEvent, shipmentId?.toString() ?? ''),
                false,
                timelineFreeEvents,
                group,
                organizations,
                undefined,
                portCallCostSharingDetails,
                isCompressedView,
                isFirstShipmentEvent,
                orderById,
              );

            addItemToGroup(group.id, timelineItem);
          }
        } else {
          const firstEvent = shipmentTds[0].event;
          const timelineItem: TimelineItem<TimelineVoyage> = buildTimelineItem(
            itemType,
            shipmentTds,
            generateItemId(true, firstEvent, shipmentId?.toString() ?? ''),
            true,
            timelineFreeEvents,
            group,
            organizations,
            batchedFreeEvents,
            portCallCostSharingDetails,
            isCompressedView,
            true, // isFirstShipmentEvent
            orderById,
          );

          addItemToGroup(group.id, timelineItem);
        }
      }
    }
  };

  for (const timeline of timelines) {
    if (timeline.type === 'spot') {
      const groupObj = groups.find(
        (g) =>
          g.timeline.type === 'spot' &&
          g.timeline.ownerOrganizationId === timeline.ownerOrganizationId &&
          g.timeline.vesselId === timeline.vesselId,
      );
      assertDefined(groupObj, 'spot group');

      buildItemBatches(
        timeline,
        isSpotEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-order-${firstEvent.order.id}`),
        organizations,
        isCompressedView,
        orderById,
      );
    } else if (timeline.type === 'coa') {
      const groupObj = groups.find(
        (g) =>
          g.timeline.type === 'coa' &&
          g.timeline.coaId === timeline.coaId &&
          g.timeline.vesselId === timeline.vesselId,
      );

      assertDefined(groupObj, 'coa group');
      buildItemBatches(
        timeline,
        isCoaEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-order-${firstEvent.order.id}`),
        organizations,
        isCompressedView,
        orderById,
      );
    } else if (timeline.type === 'tc') {
      const groupObj =
        groups.find(
          (g) => g.timeline.type === 'tc' && g.timeline === timeline,
        ) ??
        groups.find(
          (g) =>
            g.timeline.type === 'tc' &&
            g.timeline.vesselId === timeline.vesselId,
        );

      assertDefined(groupObj, 'tc group');
      buildItemBatches(
        timeline,
        isTCOrderEventTimeData,
        groupObj,
        (isBatch, firstEvent, shipmentId) =>
          timeline.id +
          (isBatch
            ? `-shipment-${shipmentId}`
            : `-${firstEvent.category}-${getEventResourceId(firstEvent)}`),
        organizations,
        isCompressedView,
        orderById,
      );

      const events = timeline.voyageGroups.flatMap((vg) => vg.events);
      const portCallEventsByPortCall = getPortCallEventsByPortCall(events);
      const renderedSingleEvents: Set<string> = new Set();

      for (const td of events) {
        if (isOffHireEventTimeData(td)) {
          // TODO: Extract timeline item construction to function for readability
          const {
            entry: { clientId },
            locationAName,
            locationBName,
          } = td.event;
          const groupObj = groups.find(
            (g) =>
              g.timeline.type === 'tc' &&
              g.timeline.vesselId === td.event.vesselId,
          );
          assertDefined(groupObj, 'group for offhire');
          const ti: TimelineItem<TimelineOffHireEvent> = {
            timeData: td,
            locationAName,
            locationBName,
            id: scenarioType
              ? `${scenarioType}-offhire-${clientId}`
              : `offhire-${clientId}`,
            vessel: td.event.vessel,
            type: ItemType.OFFHIRE,
            group: groupObj.id,
            groupObj,
            start_time: td.start,
            end_time: td.end,
          };

          addItemToGroup(groupObj.id, ti);

          continue;
        }

        if (isSingleEventTimeData(td)) {
          const {
            entry: { clientId },
            locationName,
            vessel,
            event,
            portCallId,
          } = td.event;
          if (batchedFreeEvents.has(td)) {
            continue;
          }

          const portCallEvents = portCallId
            ? portCallEventsByPortCall.get(portCallId)
            : undefined;

          const groupObj = groups.find(
            (g) =>
              g.timeline.type === 'tc' &&
              g.timeline.vesselId === td.event.vesselId,
          );
          assertDefined(groupObj, 'group for single event');
          const start_time = portCallEvents?.Berth?.start ?? td.start;
          const end_time = portCallEvents?.Depart?.end ?? td.end;

          // avoid render duplicate single events by unique key

          const key = `${event}-${start_time}-${end_time}-${locationName}-${vessel.id}`;
          if (renderedSingleEvents.has(key)) {
            continue;
          }

          const ti: TimelineItem<TimelineEvent> = {
            timeData: td,
            locationName,
            id: scenarioType
              ? `${scenarioType}-single-${clientId}`
              : `single-${clientId}`,
            vessel,
            type: ItemType.EVENT,
            eventType: event,
            group: groupObj.id,
            groupObj,
            start_time,
            end_time,
            portCallEvents,
          };

          addItemToGroup(groupObj.id, ti);
          renderedSingleEvents.add(key);

          continue;
        }

        if (isStaticEventTimeData(td) && td.event.staticEventType === 'TC_IN') {
          const timelineItem: TimelineItem<TimelineTcInEvent> = {
            end_time: td.end,
            group: groupObj.id,
            groupObj,
            id: scenarioType
              ? `${scenarioType}-tc-in-${td.event.entry.clientId}`
              : `tc-in-${td.event.entry.clientId}`,
            start_time: td.start,
            timeData: td,
            type: ItemType.TC_IN,
          };

          addItemToGroup(groupObj.id, timelineItem);

          continue;
        }
      }
    } else {
      exhaustiveCheck(
        timeline,
        `Unexpected timeline type ${(timeline as EventTimeline).type}`,
      );
    }
  }

  for (const op of charteringOpenPositions ?? []) {
    const { vesselId, startTime } = op;
    const groupObj = groups.find(
      (g) => g.timeline.type === 'tc' && g.timeline.vesselId === vesselId,
    );
    if (!groupObj) continue;

    const start = new Date(startTime);
    const end_time = new Date(start).setDate(start.getDate() + 5); // end time is hard coded
    const ti: TimelineItem<TimelineOpenPosition> = {
      type: ItemType.OPEN,
      id: `open_position_${op.id}`,
      start_time: start.getTime(),
      group: groupObj.id,
      openPosition: op,
      groupObj,
      end_time,
    };

    addItemToGroup(groupObj.id, ti);
  }

  for (const vr of vesselReservations ?? []) {
    const { vesselId, startTime, duration } = vr;
    const groupObj = groups.find(
      (g) => g.timeline.type === 'tc' && g.timeline.vesselId === vesselId,
    );
    if (!groupObj) continue;

    const start = new Date(startTime);
    const start_time = start.getTime();
    const end_time = new Date(start).setDate(start.getDate() + duration);

    const ti: TimelineItem<TimelineVesselReservation> = {
      type: ItemType.RESERVATION,
      id: `vessel_reservation_${vr.id}`,
      group: groupObj.id,
      vesselReservation: vr,
      isCompressedView,
      groupObj,
      start_time,
      end_time,
    };

    addItemToGroup(groupObj.id, ti);
  }

  // Find last non TC In event on the timeline and mark it as the last one
  // Note: groupItems is not sorted
  itemsByGroup.forEach((groupItems) => {
    let lastNonTcInItem: ScheduleItem | undefined;
    groupItems.forEach((item) => {
      if (
        item &&
        item.type !== ItemType.TC_IN &&
        (!lastNonTcInItem || item.start_time > lastNonTcInItem.start_time)
      ) {
        lastNonTcInItem = item;
      }
    });

    if (lastNonTcInItem) {
      lastNonTcInItem.isLastScheduleItem = true;
    }
  });

  return allItems;
}
